import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Building2, 
  FileText, 
  Check, 
  Loader2, 
  RefreshCw, 
  ChevronRight,
  Shield,
  Clock,
  Sparkles,
  AlertCircle,
  X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

interface PowensConnection {
  id: string;
  powens_connection_id: string;
  connector_name: string | null;
  state: string | null;
  last_sync_at: string | null;
}

interface SyncResult {
  status: string;
  imported?: number;
  skipped?: number;
  total?: number;
  subscriptions?: number;
  message?: string;
  errors?: string[];
}

interface OfficialDocumentsCardProps {
  status: "disconnected" | "connecting" | "connected" | "syncing" | "error";
  connections: PowensConnection[];
  loading: boolean;
  syncing: boolean;
  syncStatus: "idle" | "syncing" | "no_documents" | "complete" | "error";
  lastSyncResult: SyncResult | null;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync: () => void;
}

// Logo sources officielles
const OFFICIAL_SOURCES = [
  { name: "Impôts", emoji: "🏛️", color: "from-blue-500 to-blue-600" },
  { name: "Ameli", emoji: "💚", color: "from-green-500 to-green-600" },
  { name: "URSSAF", emoji: "📋", color: "from-orange-500 to-orange-600" },
  { name: "EDF", emoji: "⚡", color: "from-blue-400 to-blue-500" },
  { name: "Banques", emoji: "🏦", color: "from-purple-500 to-purple-600" },
];

export function OfficialDocumentsCard({
  status,
  connections,
  loading,
  syncing,
  syncStatus,
  lastSyncResult,
  onConnect,
  onDisconnect,
  onSync,
}: OfficialDocumentsCardProps) {
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationCount, setCelebrationCount] = useState(0);

  // Trigger celebration when import completes with documents
  useEffect(() => {
    if (syncStatus === "complete" && lastSyncResult?.imported && lastSyncResult.imported > 0) {
      setCelebrationCount(lastSyncResult.imported);
      setShowCelebration(true);
      
      // Fire confetti
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      // Hide celebration after 5 seconds
      setTimeout(() => setShowCelebration(false), 5000);
    }
  }, [syncStatus, lastSyncResult]);

  const isConnected = status === "connected" || status === "syncing";
  const isLoading = loading || status === "connecting";
  const isSyncing = syncing || status === "syncing";

  // Format last sync time
  const formatLastSync = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return "il y a quelques minutes";
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays === 1) return "hier";
    return `il y a ${diffDays} jours`;
  };

  // Get most recent sync time from connections
  const getLastSyncTime = () => {
    if (connections.length === 0) return null;
    const sorted = [...connections].sort((a, b) => {
      if (!a.last_sync_at) return 1;
      if (!b.last_sync_at) return -1;
      return new Date(b.last_sync_at).getTime() - new Date(a.last_sync_at).getTime();
    });
    return sorted[0]?.last_sync_at;
  };

  return (
    <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute inset-0 z-10 flex items-center justify-center bg-background/95 backdrop-blur-sm"
          >
            <div className="text-center space-y-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"
              >
                <Check className="w-10 h-10 text-white" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="text-2xl font-bold text-foreground">
                  {celebrationCount} document{celebrationCount > 1 ? "s" : ""} importé{celebrationCount > 1 ? "s" : ""} !
                </h3>
                <p className="text-muted-foreground mt-2">
                  Vos documents officiels sont maintenant dans EasyFinder
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCelebration(false)}
                >
                  Continuer
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/25">
                <FileText className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Documents officiels</CardTitle>
                <CardDescription className="text-xs">
                  Import automatique depuis vos organismes
                </CardDescription>
              </div>
            </div>
          </div>
          {isConnected && (
            <Badge variant="success" className="gap-1">
              <Check className="w-3 h-3" />
              Import activé
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Sources preview */}
        {!isConnected && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {OFFICIAL_SOURCES.map((source) => (
                <div
                  key={source.name}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/50 border border-border/50"
                >
                  <span className="text-sm">{source.emoji}</span>
                  <span className="text-xs font-medium text-muted-foreground">{source.name}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-secondary/30 border border-dashed border-border/50">
                <span className="text-xs text-muted-foreground">+50 sources</span>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <Shield className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                Connexion sécurisée via un partenaire agréé. Vos identifiants ne sont jamais stockés par EasyFinder.
              </p>
            </div>

            <Button
              onClick={onConnect}
              disabled={isLoading}
              className="w-full gap-2"
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connexion en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Activer l'import automatique
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Connected state */}
        {isConnected && (
          <div className="space-y-4">
            {/* Connected sources list */}
            {connections.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Sources actives
                </p>
                <div className="space-y-2">
                  {connections.map((conn) => (
                    <div
                      key={conn.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {conn.connector_name || "Source connectée"}
                          </p>
                          {conn.last_sync_at && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Dernière sync {formatLastSync(conn.last_sync_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant={conn.state === "running" || conn.state === "ready" ? "success" : "secondary"}
                        className="text-xs"
                      >
                        {conn.state === "running" || conn.state === "ready" ? "Actif" : conn.state === "wrongpass" ? "Reconnexion" : "En attente"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {connections.length === 0 && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  En attente de la synchronisation initiale...
                </p>
              </div>
            )}

            {/* Last sync result */}
            {lastSyncResult && syncStatus !== "syncing" && (
              <div className="p-3 rounded-lg bg-secondary/30 border border-border/50">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Dernière synchronisation</span>
                  <span className="font-medium">
                    {lastSyncResult.imported || 0} importé{(lastSyncResult.imported || 0) > 1 ? "s" : ""}
                    {lastSyncResult.skipped ? `, ${lastSyncResult.skipped} existant${lastSyncResult.skipped > 1 ? "s" : ""}` : ""}
                  </span>
                </div>
              </div>
            )}

            <Separator />

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onSync}
                disabled={isSyncing}
                className="flex-1 gap-2"
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Synchronisation...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Synchroniser
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onConnect}
                disabled={isLoading || isSyncing}
                className="gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Ajouter
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onDisconnect}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                title="Déconnecter toutes les sources"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Syncing state overlay */}
        {isSyncing && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div className="relative">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            </div>
            <div>
              <p className="text-sm font-medium">Synchronisation en cours...</p>
              <p className="text-xs text-muted-foreground">
                Récupération de vos documents officiels
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
